import {
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import {
  catchError,
  switchMap,
  tap,
  finalize
} from 'rxjs/operators';
import * as ngCore from '@angular/core';
import { ApiService } from '../services/api.service';
import { ApiOptimizationService } from '../services/api-optimization.service';

// Request tracking
interface RequestTracker {
  [key: string]: {
    count: number;
    lastRequest: number;
    retryCount: number;
  };
}

const requestTracker: RequestTracker = {};

export function apiOptimizationInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const injector = ngCore.inject(ngCore.Injector);

  // Get services lazily to avoid circular dependency with HttpClient
  const apiService = injector.get(ApiService);
  const apiOptimization = injector.get(ApiOptimizationService);

  // Skip Zoho requests - let ZohoSupportService handle its own auth
  const url = request.url.toLowerCase();
  const isGeminiRequest = url.includes('generativelanguage.googleapis.com') ||
    url.includes('/gemini-api/');
  const isZohoRequest = url.includes('zoho') ||
    request.headers.has('X-Zoho-Api') ||
    request.headers.get('Authorization')?.startsWith('Zoho-oauthtoken');

  if (isGeminiRequest) {
    return next(request);
  }

  if (isZohoRequest) {
    console.log('[ApiOptimizationInterceptor] BYPASSING ZOHO REQUEST:', request.url);
    const zohoRequest = request.clone({
      headers: request.headers.delete('X-Zoho-Api')
    });
    return next(zohoRequest);
  }

  // Skip optimization for certain requests
  if (shouldSkipOptimization(request)) {
    return handleBasicRequest(request, next, apiService);
  }

  // Track request
  const requestKey = generateRequestKey(request);
  trackRequest(requestKey);

  // Add auth token to request if available (authInterceptor may refine headers)
  if (!url.includes('/auth/refresh')) {
    const token = apiService.getCurrentAuthToken();
    if (token) {
      request = addToken(request, token);
    }
  }

  // Add request metadata
  request = addRequestMetadata(request);

  return next(request).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        handleSuccessfulResponse(request, event, apiOptimization);
      }
    }),
    catchError((error: HttpErrorResponse) => {
      return handleRequestError(request, error, next, apiOptimization);
    }),
    finalize(() => {
      finalizeRequest(requestKey);
    })
  );
}

function shouldSkipOptimization(request: HttpRequest<any>): boolean {
  const skipUrls = [
    '/auth/login',
    '/auth/refresh',
    '/auth/logout',
    '/auth/send-otp',
    '/auth/forget-password'
  ];

  const isExternalApi = !request.url.startsWith('/') &&
    !request.url.includes(window.location.hostname) &&
    (request.url.startsWith('http://') || request.url.startsWith('https://'));

  return skipUrls.some(url => request.url.includes(url)) || isExternalApi;
}

function handleBasicRequest(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  apiService: ApiService
): Observable<HttpEvent<any>> {
  const isExternalApi = !request.url.startsWith('/') &&
    !request.url.includes(window.location.hostname) &&
    (request.url.startsWith('http://') || request.url.startsWith('https://'));

  if (isExternalApi) {
    return next(request);
  }

  // Never attach Bearer to refresh — body carries the refresh token
  if (!request.url.toLowerCase().includes('/auth/refresh')) {
    const token = apiService.getCurrentAuthToken();
    if (token) {
      request = addToken(request, token);
    }
  }

  return next(request);
}

function addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
}

function addRequestMetadata(request: HttpRequest<any>): HttpRequest<any> {
  const isExternalApi = !request.url.startsWith('/') &&
    !request.url.includes(window.location.hostname) &&
    (request.url.startsWith('http://') || request.url.startsWith('https://'));

  if (isExternalApi) {
    return request;
  }

  return request.clone({
    setHeaders: {
      'X-Request-ID': generateRequestId(),
      'X-Request-Timestamp': Date.now().toString(),
      'X-Client-Version': '1.0.0'
    }
  });
}

function generateRequestKey(request: HttpRequest<any>): string {
  const method = request.method;
  const url = request.url;
  const params = request.params.toString();
  const body = request.body ? JSON.stringify(request.body) : '';

  return `${method}:${url}:${params}:${body}`;
}

function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function trackRequest(requestKey: string): void {
  if (!requestTracker[requestKey]) {
    requestTracker[requestKey] = {
      count: 0,
      lastRequest: Date.now(),
      retryCount: 0
    };
  }
  requestTracker[requestKey].count++;
  requestTracker[requestKey].lastRequest = Date.now();
}

function finalizeRequest(_requestKey: string): void {
  // Keep tracker entry for rate-limit / retry windows
}

function handleSuccessfulResponse(
  request: HttpRequest<any>,
  response: HttpResponse<any>,
  _apiOptimization: ApiOptimizationService
): void {
  if (request.method === 'GET' && response.status === 200) {
    // ApiOptimizationService handles caching
  }
}

function handleRequestError(
  request: HttpRequest<any>,
  error: HttpErrorResponse,
  next: HttpHandlerFn,
  _apiOptimization: ApiOptimizationService
): Observable<HttpEvent<any>> {
  const requestKey = generateRequestKey(request);
  const tracker = requestTracker[requestKey];

  console.error(`API Error: ${request.method} ${request.url}`, {
    status: error.status,
    message: error.message,
    timestamp: new Date().toISOString()
  });

  // 401 session recovery is owned by authInterceptor — do not refresh/logout here

  if (error.status === 429) {
    return handleRateLimitError(request, next, tracker);
  }

  if (error.status >= 500 && error.status < 600) {
    return handleServerError(request, next, tracker);
  }

  if (error.status === 0 || error.status === -1) {
    return handleNetworkError(request, next, tracker);
  }

  return throwError(() => error);
}

function handleRateLimitError(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  tracker: any
): Observable<HttpEvent<any>> {
  // Exponential backoff + jitter to avoid thundering herd when many clients are rate-limited simultaneously
  const baseDelay = 5000;
  const retryCount = tracker?.retryCount || 0;
  const exponentialDelay = baseDelay * Math.pow(2, retryCount);       // 5s, 10s, 20s…
  const jitter = Math.random() * 2000;                                 // 0–2s random jitter
  const retryAfter = Math.min(exponentialDelay + jitter, 60000);       // cap at 60s
  console.warn(`Rate limited: ${request.url}, retrying after ${Math.round(retryAfter)}ms (attempt ${retryCount + 1})`);
  if (tracker) tracker.retryCount = (tracker.retryCount || 0) + 1;
  return timer(retryAfter).pipe(
    switchMap(() => next(request))
  );
}

function handleServerError(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  tracker: any
): Observable<HttpEvent<any>> {
  const maxRetries = 2;
  if (!tracker) {
    return throwError(() => new HttpErrorResponse({
      error: 'Server error',
      status: 500,
      statusText: 'Internal Server Error'
    }));
  }

  if (tracker.retryCount < maxRetries) {
    tracker.retryCount++;
    const delay = Math.pow(2, tracker.retryCount) * 1000;
    console.warn(`Server error: ${request.url}, retry ${tracker.retryCount}/${maxRetries} after ${delay}ms`);
    return timer(delay).pipe(
      switchMap(() => next(request))
    );
  }

  return throwError(() => new HttpErrorResponse({
    error: 'Server error - max retries exceeded',
    status: 500,
    statusText: 'Internal Server Error'
  }));
}

/**
 * Handles network errors (status 0 or -1) with retries.
 * @param request The HTTP request object.
 * @param next The HTTP handler function.
 * @param tracker Tracking data for the current request.
 */
function handleNetworkError(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  tracker: any
): Observable<HttpEvent<any>> {
  const maxRetries = 2; // Reduced from 3 to avoid load amplification
  if (!tracker) {
    return throwError(() => new HttpErrorResponse({
      error: 'Network error',
      status: 0,
      statusText: 'Unknown Error'
    }));
  }

  if (tracker.retryCount < maxRetries) {
    tracker.retryCount++;
    const delay = Math.pow(2, tracker.retryCount) * 1000;
    console.warn(`Network error: ${request.url}, retry ${tracker.retryCount}/${maxRetries} after ${delay}ms`);
    return timer(delay).pipe(
      switchMap(() => next(request))
    );
  }

  return throwError(() => new HttpErrorResponse({
    error: 'Network error - max retries exceeded',
    status: 0,
    statusText: 'Unknown Error'
  }));
}
